/* Aurzart Home — interactions for aurz-* sections. No dependencies. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(pointer: fine)').matches;

/* ---------- Shared audio ---------- */
let audioCtx = null;
let masterGain = null;

function getAudio() {
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
    const comp = audioCtx.createDynamicsCompressor();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = 0.5;
    masterGain.connect(comp).connect(audioCtx.destination);
  }
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

/** Soft piano-like tone: layered partials with a hammer transient and exponential decay. */
function playPiano(freq, velocity = 0.8) {
  const ctx = getAudio();
  if (!ctx) return;
  const now = ctx.currentTime;
  const out = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(Math.min(9000, freq * 9), now);
  filter.frequency.exponentialRampToValueAtTime(Math.max(600, freq * 2), now + 1.6);
  out.connect(filter).connect(masterGain);

  const decay = 2.8 - Math.log2(freq / 261.63) * 0.6;
  out.gain.setValueAtTime(0, now);
  out.gain.linearRampToValueAtTime(0.32 * velocity, now + 0.006);
  out.gain.exponentialRampToValueAtTime(0.12 * velocity, now + 0.35);
  out.gain.exponentialRampToValueAtTime(0.0001, now + decay);

  const partials = [
    [1, 1, 'triangle'],
    [2, 0.42, 'sine'],
    [3, 0.18, 'sine'],
    [4, 0.08, 'sine'],
  ];
  for (const [mult, amp, type] of partials) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq * mult * (1 + (mult - 1) * 0.0008);
    osc.detune.value = (Math.random() - 0.5) * 4;
    g.gain.value = amp;
    osc.connect(g).connect(out);
    osc.start(now);
    osc.stop(now + decay + 0.1);
  }
}

function click(accent) {
  const ctx = getAudio();
  if (!ctx) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = accent ? 1760 : 1175;
  g.gain.setValueAtTime(accent ? 0.5 : 0.3, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
  osc.connect(g).connect(masterGain);
  osc.start(now);
  osc.stop(now + 0.07);
}

/* ---------- Instrument hover sounds ---------- */
// Short synthesized previews (no audio files). Share the keyboard's mute preference.
function isMuted() {
  try {
    return localStorage.getItem('az-keys-muted') === '1';
  } catch (e) {
    return false;
  }
}

function noiseBuffer(ctx, seconds) {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

function voiceOut(ctx, level = 0.8) {
  const g = ctx.createGain();
  g.gain.value = level;
  g.connect(masterGain);
  return g;
}

const SOUNDS = {
  piano(ctx) {
    // Gentle rolled Cmaj9 chord
    [261.63, 329.63, 392.0, 493.88, 587.33].forEach((f, i) => setTimeout(() => playPiano(f, 0.55), i * 55));
  },
  guitar(ctx) {
    // Karplus-Strong strum: E A D G B E (open G-ish voicing)
    const now = ctx.currentTime;
    const out = voiceOut(ctx, 0.55);
    [98.0, 146.83, 196.0, 246.94, 293.66, 392.0].forEach((f, i) => {
      const t = now + i * 0.028;
      const period = Math.round(ctx.sampleRate / f);
      const len = ctx.sampleRate * 2.2;
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let n = 0; n < period; n++) d[n] = Math.random() * 2 - 1;
      for (let n = period + 1; n < len; n++) d[n] = 0.996 * 0.5 * (d[n - period] + d[n - period - 1]);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 3200;
      src.connect(lp).connect(out);
      src.start(t);
    });
  },
  drums(ctx) {
    // Kick · hat · snare · hat · kick-kick · snare
    const now = ctx.currentTime;
    const out = voiceOut(ctx, 0.9);
    const step = 0.13;
    const kick = (t) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.setValueAtTime(140, t);
      o.frequency.exponentialRampToValueAtTime(42, t + 0.18);
      g.gain.setValueAtTime(0.9, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      o.connect(g).connect(out);
      o.start(t);
      o.stop(t + 0.32);
    };
    const noise = (t, dur, freq, type, level) => {
      const src = ctx.createBufferSource();
      src.buffer = noiseBuffer(ctx, dur);
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(level, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      src.connect(f).connect(g).connect(out);
      src.start(t);
    };
    const snare = (t) => {
      noise(t, 0.2, 1800, 'bandpass', 0.6);
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = 190;
      g.gain.setValueAtTime(0.35, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
      o.connect(g).connect(out);
      o.start(t);
      o.stop(t + 0.14);
    };
    const hat = (t) => noise(t, 0.05, 8000, 'highpass', 0.25);
    kick(now);
    hat(now + step);
    snare(now + step * 2);
    hat(now + step * 3);
    kick(now + step * 4);
    kick(now + step * 5);
    snare(now + step * 6);
    hat(now + step * 6);
  },
  violin(ctx) {
    // Bowed phrase: A4 → C#5 → E5 with vibrato and soft attack
    const now = ctx.currentTime;
    const out = voiceOut(ctx, 0.32);
    const notes = [
      [440, 0, 0.55],
      [554.37, 0.5, 0.5],
      [659.25, 0.95, 0.9],
    ];
    notes.forEach(([f, start, dur]) => {
      const t = now + start;
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      const vib = ctx.createOscillator();
      const vibGain = ctx.createGain();
      vib.frequency.value = 5.5;
      vibGain.gain.setValueAtTime(0, t);
      vibGain.gain.linearRampToValueAtTime(f * 0.006, t + 0.25);
      vib.connect(vibGain).connect(o.frequency);
      const bp = ctx.createBiquadFilter();
      bp.type = 'lowpass';
      bp.frequency.value = 2600;
      bp.Q.value = 2;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.7, t + 0.12);
      g.gain.setValueAtTime(0.7, t + dur - 0.08);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.25);
      o.connect(bp).connect(g).connect(out);
      o.start(t);
      vib.start(t);
      o.stop(t + dur + 0.3);
      vib.stop(t + dur + 0.3);
    });
  },
  chime(ctx) {
    // Soft two-note chime for accessories / misc
    const now = ctx.currentTime;
    const out = voiceOut(ctx, 0.35);
    [[1318.5, 0], [1760, 0.12]].forEach(([f, d]) => {
      const t = now + d;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.5, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
      o.connect(g).connect(out);
      o.start(t);
      o.stop(t + 1.3);
    });
  },
};

// Browsers only allow audio after a user gesture: unlock on the first interaction
let audioUnlocked = false;
const unlockAudio = () => {
  if (audioUnlocked) return;
  audioUnlocked = true;
  getAudio();
};
['pointerdown', 'keydown', 'touchstart'].forEach((ev) =>
  window.addEventListener(ev, unlockAudio, { once: true, passive: true, capture: true })
);

let lastSoundAt = 0;
let lastSoundEl = null;
document.addEventListener(
  'pointerover',
  (e) => {
    if (!finePointer || !audioUnlocked || isMuted()) return;
    const el = e.target.closest('[data-az-sound]');
    if (!el || el === lastSoundEl || el.contains(e.relatedTarget)) return;
    const now = performance.now();
    if (now - lastSoundAt < 450) return;
    const kind = el.dataset.azSound;
    if (!SOUNDS[kind] || !audioCtx || audioCtx.state !== 'running') return;
    lastSoundEl = el;
    lastSoundAt = now;
    SOUNDS[kind](audioCtx);
    document.dispatchEvent(new CustomEvent('az:sound', { detail: kind }));
  },
  { passive: true }
);
document.addEventListener(
  'pointerout',
  (e) => {
    const el = e.target.closest('[data-az-sound]');
    if (el && !el.contains(e.relatedTarget) && el === lastSoundEl) lastSoundEl = null;
  },
  { passive: true }
);

/* ---------- Reveal on scroll ---------- */
const revealIO = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-in');
        entry.target.dispatchEvent(new CustomEvent('az:in'));
        revealIO.unobserve(entry.target);
      }
    }
  },
  { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
);

function initReveals(root = document) {
  root.querySelectorAll('[data-az-reveal]:not(.is-in), .az-split-line:not(.is-in)').forEach((el) => {
    if (reduceMotion) el.classList.add('is-in');
    else revealIO.observe(el);
  });
}

/* ---------- Parallax (cheap, rAF-batched) ---------- */
const parallaxEls = new Set();
let parallaxTick = false;

function updateParallax() {
  parallaxTick = false;
  const vh = window.innerHeight;
  for (const el of parallaxEls) {
    const r = el.getBoundingClientRect();
    if (r.bottom < -200 || r.top > vh + 200) continue;
    const progress = (r.top + r.height / 2 - vh / 2) / vh; // -1..1
    const speed = parseFloat(el.dataset.azParallax) || 40;
    el.style.setProperty('--az-par', `${(-progress * speed).toFixed(1)}px`);
  }
}

function initParallax(root = document) {
  if (reduceMotion) return;
  root.querySelectorAll('[data-az-parallax]').forEach((el) => parallaxEls.add(el));
  updateParallax();
}

// Horizon scrolls inside .page-wrapper, so listen in the capture phase to catch any scroller
document.addEventListener(
  'scroll',
  () => {
    if (!parallaxTick && parallaxEls.size) {
      parallaxTick = true;
      requestAnimationFrame(updateParallax);
    }
  },
  { passive: true, capture: true }
);

/* ---------- Hero slideshow ---------- */
class AzHero extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.az-slide')];
    this.tabs = [...this.querySelectorAll('.az-hero__tab')];
    this.index = 0;
    this.duration = (Number(this.dataset.autoplay) || 7) * 1000;
    this.syncVideos();

    if (this.slides.length > 1) {
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.go(Number(tab.dataset.go))));

      // Pause while the visitor is reading or playing the keyboard
      this.addEventListener('pointerenter', () => this.pause());
      this.addEventListener('pointerleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.resume());
      document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.resume()));

      // Swipe
      let x0 = null;
      const stage = this.querySelector('.az-slides');
      stage.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
      stage.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      });

      if (!reduceMotion) this.schedule();
    }

    // Theme editor: jump to the selected slide
    document.addEventListener('shopify:block:select', (e) => {
      const i = this.slides.indexOf(e.target);
      if (i > -1) {
        this.go(i);
        this.pause();
      }
    });

    if (!finePointer || reduceMotion) return;
    this.onMove = (e) => {
      const r = this.getBoundingClientRect();
      this.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
      this.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
    };
    this.addEventListener('pointermove', this.onMove);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  go(i) {
    const n = this.slides.length;
    const next = ((i % n) + n) % n;
    if (next === this.index) return;
    this.slides[this.index].classList.remove('is-active');
    this.slides[this.index].setAttribute('aria-hidden', 'true');
    this.tabs[this.index]?.classList.remove('is-active');
    this.tabs[this.index]?.setAttribute('aria-selected', 'false');
    this.index = next;
    this.slides[next].classList.add('is-active');
    this.slides[next].removeAttribute('aria-hidden');
    const tab = this.tabs[next];
    if (tab) {
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');
      // restart progress animation
      const bar = tab.querySelector('.az-hero__tab-bar i');
      if (bar) {
        bar.style.animation = 'none';
        void bar.offsetWidth;
        bar.style.animation = '';
      }
    }
    this.syncVideos();
    if (this.paused) {
      // Restart the full interval once the visitor moves away
      this.remaining = this.duration;
    } else {
      this.schedule();
    }
  }

  syncVideos() {
    this.slides.forEach((slide, i) => {
      const v = slide.querySelector('video');
      if (!v) return;
      if (i === this.index && !reduceMotion) {
        v.muted = true;
        if (v.preload === 'none') v.preload = 'auto';
        v.play?.().catch(() => {});
      } else {
        v.pause?.();
      }
    });
  }

  schedule() {
    clearTimeout(this.timer);
    if (this.slides.length < 2 || reduceMotion) return;
    this.started = performance.now();
    this.remaining = this.duration;
    this.timer = setTimeout(() => this.go(this.index + 1), this.duration);
  }

  pause() {
    if (this.paused || this.slides.length < 2) return;
    this.paused = true;
    this.classList.add('is-paused');
    clearTimeout(this.timer);
    this.remaining = Math.max(0, this.remaining - (performance.now() - this.started));
  }

  resume() {
    if (!this.paused || reduceMotion) return;
    this.paused = false;
    this.classList.remove('is-paused');
    this.started = performance.now();
    this.timer = setTimeout(() => this.go(this.index + 1), this.remaining);
  }
}

/* ---------- Playable keyboard ---------- */
const KEY_MAP = ['a', 'w', 's', 'e', 'd', 'f', 't', 'g', 'y', 'h', 'u', 'j', 'k', 'o', 'l', 'p', ';'];

class AzKeyboard extends HTMLElement {
  connectedCallback() {
    this.muted = this.dataset.sound !== 'true';
    try {
      if (localStorage.getItem('az-keys-muted') === '1') this.muted = true;
    } catch (e) {}
    this.keys = [...this.querySelectorAll('.az-key')];
    this.muteBtn = this.querySelector('.az-mute');
    this.syncMute();

    let dragging = false;
    let lastKey = null;

    const press = (key, velocity = 0.8) => {
      if (!key || key === lastKey) return;
      lastKey = key;
      this.trigger(key, velocity);
    };

    this.addEventListener('pointerdown', (e) => {
      const key = e.target.closest('.az-key');
      if (!key) return;
      dragging = true;
      lastKey = null;
      press(key, 0.9);
    });

    this.addEventListener('pointermove', (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const key = el && el.closest('.az-key');
      if (dragging) {
        press(key, 0.75);
      } else if (finePointer && key && key !== this.hoverKey) {
        // Gentle glissando on hover
        this.hoverKey = key;
        this.trigger(key, 0.45);
      }
    });

    this.addEventListener('pointerleave', () => {
      this.hoverKey = null;
    });

    window.addEventListener('pointerup', () => {
      dragging = false;
      lastKey = null;
    });

    this.muteBtn?.addEventListener('click', () => {
      this.muted = !this.muted;
      try {
        localStorage.setItem('az-keys-muted', this.muted ? '1' : '0');
      } catch (e) {}
      if (!this.muted) getAudio();
      this.syncMute();
    });

    // Computer keyboard: A W S E D F T G Y H U J K … while hero is visible
    this.visible = false;
    new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
    }).observe(this);

    this.onKey = (e) => {
      if (!this.visible || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const idx = KEY_MAP.indexOf(e.key.toLowerCase());
      if (idx === -1 || !this.keys[idx]) return;
      const key = this.keys.find((k) => Number(k.dataset.index) === idx);
      if (key && key.offsetParent !== null) this.trigger(key, 0.85);
    };
    document.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey);
  }

  syncMute() {
    if (!this.muteBtn) return;
    this.muteBtn.setAttribute('aria-pressed', String(!this.muted));
    this.muteBtn.querySelector('[data-on]').hidden = this.muted;
    this.muteBtn.querySelector('[data-off]').hidden = !this.muted;
  }

  trigger(key, velocity) {
    key.classList.add('is-down');
    clearTimeout(key._t);
    key._t = setTimeout(() => key.classList.remove('is-down'), 220);
    if (!this.muted) playPiano(Number(key.dataset.freq), velocity);

    if (!reduceMotion && velocity > 0.6) {
      const burst = document.createElement('span');
      burst.className = 'az-note-burst';
      burst.textContent = Math.random() > 0.5 ? '♪' : '♫';
      const kr = key.getBoundingClientRect();
      const hr = this.querySelector('.az-keys').getBoundingClientRect();
      burst.style.left = `${kr.left - hr.left + kr.width / 2}px`;
      this.querySelector('.az-keys').appendChild(burst);
      burst.addEventListener('animationend', () => burst.remove());
    }
  }
}

/* ---------- Category tilt + glow ---------- */
class AzTilt extends HTMLElement {
  connectedCallback() {
    if (!finePointer || reduceMotion) return;
    this.querySelectorAll('.az-cat').forEach((card) => {
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        card.style.setProperty('--mx', `${x * 100}%`);
        card.style.setProperty('--my', `${y * 100}%`);
        card.style.setProperty('--ry', `${(x - 0.5) * 5}deg`);
        card.style.setProperty('--rx', `${(0.5 - y) * 5}deg`);
      });
      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
      });
    });
  }
}

/* ---------- Counters + hotspots ---------- */
class AzSpotlight extends HTMLElement {
  connectedCallback() {
    const stats = this.querySelector('.az-stats');
    if (stats) {
      stats.addEventListener('az:in', () => this.countUp(), { once: true });
      if (reduceMotion) this.countUp(true);
    }

    this.querySelectorAll('.az-hotspot').forEach((spot) => {
      spot.querySelector('.az-hotspot__dot')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = spot.classList.toggle('is-open');
        spot.querySelector('.az-hotspot__dot').setAttribute('aria-expanded', String(open));
        this.querySelectorAll('.az-hotspot.is-open').forEach((s) => s !== spot && s.classList.remove('is-open'));
      });
    });
    document.addEventListener('click', () => {
      this.querySelectorAll('.az-hotspot.is-open').forEach((s) => s.classList.remove('is-open'));
    });
  }

  countUp(instant = false) {
    this.querySelectorAll('[data-count-to]').forEach((el) => {
      const target = parseFloat(el.dataset.countTo);
      const decimals = (el.dataset.countTo.split('.')[1] || '').length;
      if (instant || Number.isNaN(target)) {
        el.textContent = el.dataset.countTo;
        return;
      }
      const start = performance.now();
      const dur = 1800;
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 4);
        el.textContent = (target * eased).toFixed(decimals);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }
}

/* ---------- Rentals: list drives image ---------- */
class AzRentals extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.az-space')];
    this.imgs = [...this.querySelectorAll('.az-rentals__img')];
    this.items.forEach((item, i) => {
      const head = item.querySelector('.az-space__head');
      head.addEventListener('click', () => this.activate(i));
      if (finePointer) head.addEventListener('pointerenter', () => this.activate(i));
    });
  }

  activate(i) {
    this.items.forEach((item, j) => {
      item.classList.toggle('is-active', i === j);
      item.querySelector('.az-space__head').setAttribute('aria-expanded', String(i === j));
    });
    this.imgs.forEach((img, j) => img.classList.toggle('is-active', i === j));
  }
}

/* ---------- Metronome ---------- */
const TEMPO_NAMES = [
  [40, 'Grave'],
  [60, 'Largo'],
  [66, 'Adagio'],
  [76, 'Andante'],
  [108, 'Moderato'],
  [120, 'Allegretto'],
  [156, 'Allegro'],
  [176, 'Vivace'],
  [200, 'Presto'],
  [999, 'Prestissimo'],
];

class AzMetronome extends HTMLElement {
  connectedCallback() {
    this.bpm = Number(this.dataset.bpm) || 96;
    this.beatsPerBar = 4;
    this.beat = 0;
    this.running = false;
    this.taps = [];

    this.$bpm = this.querySelector('[data-bpm]');
    this.$tempo = this.querySelector('[data-tempo]');
    this.$range = this.querySelector('input[type="range"]');
    this.$play = this.querySelector('[data-play]');
    this.$beats = [...this.querySelectorAll('.az-metro__beat')];
    this.$arm = this.querySelector('.az-metro__arm');

    this.$range?.addEventListener('input', () => this.setBpm(Number(this.$range.value)));
    this.querySelector('[data-minus]')?.addEventListener('click', () => this.setBpm(this.bpm - 1));
    this.querySelector('[data-plus]')?.addEventListener('click', () => this.setBpm(this.bpm + 1));
    this.querySelector('[data-tap]')?.addEventListener('click', () => this.tap());
    this.$play?.addEventListener('click', () => (this.running ? this.stop() : this.start()));

    this.setBpm(this.bpm);
  }

  disconnectedCallback() {
    this.stop();
  }

  setBpm(v) {
    this.bpm = Math.max(30, Math.min(240, Math.round(v)));
    if (this.$bpm) this.$bpm.textContent = this.bpm;
    if (this.$range) {
      this.$range.value = this.bpm;
      const pct = ((this.bpm - 30) / 210) * 100;
      this.$range.style.setProperty('--az-fill', `${pct}%`);
    }
    if (this.$tempo) this.$tempo.textContent = TEMPO_NAMES.find(([max]) => this.bpm < max)[1];
  }

  tap() {
    const now = performance.now();
    this.taps = this.taps.filter((t) => now - t < 2500);
    this.taps.push(now);
    if (this.taps.length >= 2) {
      const gaps = this.taps.slice(1).map((t, i) => t - this.taps[i]);
      const avg = gaps.reduce((a, b) => a + b, 0) / gaps.length;
      this.setBpm(60000 / avg);
    }
    click(false);
  }

  start() {
    if (!getAudio()) return;
    this.running = true;
    this.beat = 0;
    this.nextTime = audioCtx.currentTime + 0.05;
    this.$play.textContent = this.$play.dataset.stopLabel || 'Stop';
    this.$play.setAttribute('aria-pressed', 'true');
    this.schedule();
  }

  stop() {
    this.running = false;
    clearTimeout(this.timer);
    if (this.$play) {
      this.$play.textContent = this.$play.dataset.startLabel || 'Start';
      this.$play.setAttribute('aria-pressed', 'false');
    }
    this.$beats?.forEach((b) => b.classList.remove('is-on'));
    this.$arm?.style.setProperty('--az-swing', '0deg');
  }

  // Lookahead scheduler for sample-accurate timing
  schedule() {
    if (!this.running) return;
    while (this.nextTime < audioCtx.currentTime + 0.1) {
      const beat = this.beat;
      const when = this.nextTime;
      this.scheduleClick(when, beat === 0);
      const delay = Math.max(0, (when - audioCtx.currentTime) * 1000);
      setTimeout(() => this.visual(beat), delay);
      this.nextTime += 60 / this.bpm;
      this.beat = (this.beat + 1) % this.beatsPerBar;
    }
    this.timer = setTimeout(() => this.schedule(), 25);
  }

  scheduleClick(when, accent) {
    const osc = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = accent ? 1760 : 1175;
    g.gain.setValueAtTime(accent ? 0.5 : 0.3, when);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.06);
    osc.connect(g).connect(masterGain);
    osc.start(when);
    osc.stop(when + 0.07);
  }

  visual(beat) {
    if (!this.running) return;
    this.$beats.forEach((b, i) => b.classList.toggle('is-on', i === beat));
    if (this.$arm && !reduceMotion) {
      const dir = beat % 2 === 0 ? 1 : -1;
      this.$arm.style.transition = `transform ${60 / this.bpm}s cubic-bezier(0.45, 0, 0.55, 1)`;
      this.$arm.style.setProperty('--az-swing', `${dir * 24}deg`);
    }
  }
}

/* ---------- Read more (product description) ---------- */
class AzReadMore extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector('.az-desc__toggle');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const open = this.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(open));
      btn.querySelector('span').textContent = open ? btn.dataset.less : btn.dataset.more;
      if (!open) this.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }
}

/* ---------- Product showcase: count-up + scroll-synced story ---------- */
class AzShowcase extends HTMLElement {
  connectedCallback() {
    // Count-up numbers when the stats row enters
    const stats = this.querySelector('.az-show__stats');
    if (stats && !reduceMotion) {
      const nums = [...stats.querySelectorAll('[data-count-to]')];
      nums.forEach((el) => (el.textContent = '0'));
      new IntersectionObserver((entries, io) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        nums.forEach((el) => {
          const target = parseFloat(el.dataset.countTo);
          if (Number.isNaN(target)) return (el.textContent = el.dataset.countTo);
          const start = performance.now();
          const step = (now) => {
            const t = Math.min(1, (now - start) / 1600);
            el.textContent = Math.round(target * (1 - Math.pow(1 - t, 4)));
            if (t < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        });
      }, { threshold: 0.4 }).observe(stats);
    }

    // Activate the frame that matches the step in the middle of the viewport
    const steps = [...this.querySelectorAll('.az-show__step')];
    const frames = [...this.querySelectorAll('.az-show__frame')];
    if (!steps.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const i = entry.target.dataset.index;
          steps.forEach((s) => s.classList.toggle('is-active', s.dataset.index === i));
          frames.forEach((f) => f.classList.toggle('is-active', f.dataset.index === i));
        });
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    steps.forEach((s) => io.observe(s));
  }
}

/* ---------- Product carousels: clear prev/next + progress ---------- */
function enhanceCarousels(root = document) {
  root.querySelectorAll('.resource-list__carousel').forEach((carousel) => {
    if (carousel.dataset.azControls) return;
    // Horizon nests two elements with this class; attach to the innermost only
    if (carousel.querySelector('.resource-list__carousel')) return;
    const track = carousel.querySelector('slideshow-slides');
    if (!track) return;
    // Only the cards themselves (product cards contain their own image slideshows)
    const slides = track.querySelectorAll(':scope > slideshow-slide');
    if (slides.length < 2) return;
    carousel.dataset.azControls = '1';

    const bar = document.createElement('div');
    bar.className = 'az-carousel-bar';
    bar.innerHTML =
      '<button type="button" class="az-carousel-btn" data-dir="-1" aria-label="Previous products">' +
      '<svg viewBox="0 0 24 12" fill="none" aria-hidden="true"><path d="M24 6H2M7 1L2 6l5 5" stroke="currentColor" stroke-width="1.3"/></svg></button>' +
      '<div class="az-carousel-track" aria-hidden="true"><i></i></div>' +
      '<span class="az-carousel-count" aria-live="polite"></span>' +
      '<button type="button" class="az-carousel-btn" data-dir="1" aria-label="Next products">' +
      '<svg viewBox="0 0 24 12" fill="none" aria-hidden="true"><path d="M0 6h22M17 1l5 5-5 5" stroke="currentColor" stroke-width="1.3"/></svg></button>';
    carousel.after(bar);

    const [prev, next] = bar.querySelectorAll('.az-carousel-btn');
    const fill = bar.querySelector('.az-carousel-track i');
    const count = bar.querySelector('.az-carousel-count');
    const total = slides.length;

    const step = () => {
      const first = slides[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      const perView = Math.max(1, Math.floor((track.clientWidth + gap) / (first + gap)));
      return { size: (first + gap) * perView, perView, unit: first + gap };
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const x = Math.abs(track.scrollLeft);
      const { perView, unit } = step();
      const seen = Math.min(total, Math.round(x / unit) + perView);
      fill.style.transform = `scaleX(${Math.max(perView / total, max > 0 ? seen / total : 1)})`;
      count.textContent = `${seen} / ${total}`;
      prev.disabled = x <= 2;
      next.disabled = x >= max - 2;
      bar.hidden = max <= 2 || carousel.offsetParent === null;
    };

    bar.addEventListener('click', (e) => {
      const btn = e.target.closest('.az-carousel-btn');
      if (!btn || btn.disabled) return;
      track.scrollBy({ left: Number(btn.dataset.dir) * step().size, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
    setTimeout(update, 600);
  });
}

/* ---------- Register ---------- */
/* ---------- Product gallery: detect cut-out (transparent) photos ----------
   Cut-out PNGs sit on a dark spotlight stage; white-background photos keep the
   ivory stage. A 48px copy is sampled at its edges (the CDN allows CORS). */
const cutoutCache = new Map();

function isCutout(src) {
  if (!cutoutCache.has(src)) {
    cutoutCache.set(
      src,
      new Promise((resolve) => {
        const url = new URL(src, location.href);
        url.searchParams.set('width', '48');
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.onload = () => {
          try {
            const c = document.createElement('canvas');
            c.width = c.height = 48;
            const ctx = c.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(probe, 0, 0, 48, 48);
            const px = ctx.getImageData(0, 0, 48, 48).data;
            let clear = 0;
            let total = 0;
            for (let i = 0; i < 48; i++) {
              for (const [x, y] of [[i, 0], [i, 47], [0, i], [47, i]]) {
                total++;
                if (px[(y * 48 + x) * 4 + 3] < 16) clear++;
              }
            }
            resolve(clear / total > 0.9);
          } catch {
            resolve(false);
          }
        };
        probe.onerror = () => resolve(false);
        probe.src = url.href;
      })
    );
  }
  return cutoutCache.get(src);
}

function markCutouts(root = document) {
  if (!document.body.classList.contains('template-product')) return;
  root.querySelectorAll('.product-media img:not([data-az-cut])').forEach((img) => {
    const src = img.currentSrc || img.src;
    if (!src || !src.includes('.png')) return;
    img.dataset.azCut = '';
    isCutout(src).then((cut) => {
      if (cut) (img.closest('.product-media-container') || img.closest('.product-media'))?.classList.add('az-cutout');
    });
  });
}

const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);
define('az-hero', AzHero);
define('az-keyboard', AzKeyboard);
define('az-tilt', AzTilt);
define('az-spotlight', AzSpotlight);
define('az-metronome', AzMetronome);
define('az-rentals', AzRentals);
define('az-readmore', AzReadMore);
define('az-showcase', AzShowcase);

initReveals();
initParallax();
enhanceCarousels();
markCutouts();
window.addEventListener('load', () => {
  enhanceCarousels();
  markCutouts();
});

// Variant changes re-render the gallery
if (document.body.classList.contains('template-product')) {
  new MutationObserver(() => markCutouts()).observe(document.body, { childList: true, subtree: true });
}

// Theme editor: re-init when sections are re-rendered
document.addEventListener('shopify:section:load', (e) => {
  initReveals(e.target);
  initParallax(e.target);
  enhanceCarousels(e.target);
  markCutouts(e.target);
});
